import { useCallback, useEffect, useRef, useState } from 'react';
import { DASHBOARD_HELP_REQUEST } from '../../../../utils/dashboardHelp';
import { DASHBOARD_ASSISTANT_DOCKED_BREAKPOINT } from '../lib/panelLayout';

type Surface = 'none' | 'ai' | 'help-menu' | 'help-widget';
type Widget = {
  open?: () => void;
  close?: () => void;
  set?: (options: { position: string; offset: { bottom: string } }) => void;
  on?: (event: string, handler: () => void) => void;
  off?: (event: string, handler: () => void) => void;
};
const widget = () => (window as Window & { Productlane?: Widget }).Productlane;

export function useCompactAssistantViewport() {
  const [compact, setCompact] = useState(() => window.innerWidth < DASHBOARD_ASSISTANT_DOCKED_BREAKPOINT);
  useEffect(() => {
    const query = window.matchMedia(`(min-width: ${DASHBOARD_ASSISTANT_DOCKED_BREAKPOINT}px)`);
    const update = () => setCompact(!query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return compact;
}

/** Owns visibility only. Conversation state and the vendor's DOM remain with their owners. */
export function useAssistantSurfaces(scope: string, hideAI: () => void) {
  const isCompact = useCompactAssistantViewport();
  const [surface, setSurface] = useState<Surface>('none');
  const [isProductlaneOpen, setProductlaneOpen] = useState(false);
  const [helpError, setHelpError] = useState<string | null>(null);
  const attachRef = useRef<(() => void) | undefined>(undefined);
  const intent = useRef<Surface>('none');
  const generation = useRef(0);
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const compactRef = useRef(isCompact);
  compactRef.current = isCompact;
  const request = useCallback((next: Surface) => {
    generation.current += 1;
    clearTimeout(timeout.current);
    timeout.current = undefined;
    intent.current = next;
    setSurface(next);
    setHelpError(null);
    return generation.current;
  }, []);
  const closeWidget = useCallback(() => {
    try { widget()?.close?.(); } catch { /* A blocked widget must not block AI. */ }
    setProductlaneOpen(false);
  }, []);
  const showAI = useCallback(() => {
    request('ai');
    if (compactRef.current) closeWidget();
  }, [closeWidget, request]);
  const dismissSurfaces = useCallback(() => {
    request('none');
    hideAI();
    closeWidget();
  }, [closeWidget, hideAI, request]);
  const showHelpMenu = useCallback(() => {
    request('help-menu');
    if (compactRef.current) { hideAI(); closeWidget(); }
  }, [closeWidget, hideAI, request]);
  const closeHelpMenu = useCallback(() => {
    if (intent.current === 'help-menu') request('none');
  }, [request]);
  const openHelp = useCallback(() => {
    attachRef.current?.();
    const api = widget();
    if (!api?.open || !api.close || !api.on) {
      if (compactRef.current && intent.current !== 'ai') request('help-menu');
      setHelpError('Help Center is unavailable. Please try again.');
      return;
    }
    const token = request('help-widget');
    if (compactRef.current) hideAI();
    // Let Radix release focus/scroll ownership before the native widget opens.
    timeout.current = setTimeout(() => {
      if (generation.current !== token) return;
      const fail = () => {
        if (generation.current !== token) return;
        closeWidget();
        request('help-menu');
        setHelpError('Help Center could not open. Please try again.');
      };
      timeout.current = setTimeout(fail, 5000);
      try { api.open?.(); } catch { fail(); }
    }, 0);
  }, [closeWidget, hideAI, request]);

  useEffect(() => {
    const handleRequest = (event: Event) => { event.preventDefault(); openHelp(); };
    window.addEventListener(DASHBOARD_HELP_REQUEST, handleRequest);
    return () => window.removeEventListener(DASHBOARD_HELP_REQUEST, handleRequest);
  }, [openHelp]);

  useEffect(() => {
    // Scope changes invalidate pending opens and never carry a support surface to another org.
    dismissSurfaces();
    return () => { generation.current += 1; clearTimeout(timeout.current); };
  }, [scope, dismissSurfaces]);

  useEffect(() => {
    let attached: Widget | undefined;
    const position = () => {
      try { attached?.set?.({ position: isCompact ? 'center' : 'right', offset: { bottom: isCompact ? '8px' : '20px' } }); } catch { /* Native layout remains usable. */ }
    };
    const opened = () => {
      if (compactRef.current && intent.current !== 'help-widget') {
        closeWidget();
        return;
      }
      clearTimeout(timeout.current);
      timeout.current = undefined;
      setProductlaneOpen(true);
      if (compactRef.current) hideAI();
    };
    const closed = () => {
      setProductlaneOpen(false);
      // A close from an earlier transition must not cancel an in-flight open.
      if (intent.current === 'help-widget' && !timeout.current) {
        const token = request('none');
        requestAnimationFrame(() => {
          if (generation.current === token && compactRef.current) {
            document.querySelector<HTMLButtonElement>('[data-mobile-more], [aria-label="Open help menu"]')?.focus();
          }
        });
      }
    };
    const detach = () => {
      attached?.off?.('opened', opened);
      attached?.off?.('closed', closed);
      attached?.off?.('loaded', position);
    };
    const attach = () => {
      const next = widget();
      if (next === attached || !next?.on) return;
      detach();
      attached = next;
      next.on('opened', opened);
      next.on('closed', closed);
      next.on('loaded', position);
      position();
    };
    attachRef.current = attach;
    attach();
    // The SDK can be injected after authentication, or replace its queued stub.
    const interval = setInterval(attach, 500);
    if (isCompact && intent.current !== 'help-widget') closeWidget();
    if (!isCompact && intent.current === 'help-menu') request('none');
    return () => { attachRef.current = undefined; clearInterval(interval); detach(); };
  }, [closeWidget, hideAI, isCompact, request]);

  return { isCompact, surface, isProductlaneOpen, helpError, showAI, showHelpMenu, closeHelpMenu, openHelp, closeWidget, dismissSurfaces };
}
